import { PRE_ONBOARDING_JOURNEY_RETURN_PARAM } from '../pages/vms/preOnboardingInProgressModel';

/** Pre-onboarding list roots — workspace service changes stay on these, not child routes. */
const PRE_ONBOARDING_HUB_PATHS = new Set([
  '/vms/pre-onboarding/in-progress',
  '/vms/pre-onboarding/vendor-initiation',
  '/vms/pre-onboarding/overview',
]);

/** Legacy module paths opened from the pre-onboarding journey (outside `/vms/pre-onboarding/*`). */
const PRE_ONBOARDING_LEGACY_PREFIXES = [
  '/vms/rfp-management',
  '/vms/prospective-vendors',
  '/vms/vendor-proposals',
  '/vms/vendor-evaluations',
  '/vms/financial-approval',
  '/vms/vendor-initiation-requests',
  '/vms/vendor-initiation',
  '/vms/materiality-assessment',
  '/vms/pending-vendor-initiation-annexures',
] as const;

const ONBOARDING_HUB_PATH = '/vms/onboarding';
const ONGOING_HUB_PATH = '/vms/ongoing';

/** Query keys that mean the ongoing hub is showing a vendor/batch/annexure drill-in (not the list). */
const ONGOING_DEEP_QUERY_KEYS = [
  'assessment',
  'assessment_type',
  'ongoing_batch_id',
  'vms_outsourcing_vendor_id',
  'vms_registered_vendor_id',
  'annexure',
] as const;

function normalizePath(pathname: string): string {
  const trimmed = pathname.replace(/\/$/, '');
  return trimmed || '/';
}

function parseSearch(search: string): URLSearchParams {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(raw);
}

function matchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

function isPreOnboardingLegacyModulePath(path: string): boolean {
  return PRE_ONBOARDING_LEGACY_PREFIXES.some((prefix) => matchesPrefix(path, prefix));
}

function isPreOnboardingWorkspaceRoute(path: string, searchParams: URLSearchParams): boolean {
  if (path.startsWith('/vms/pre-onboarding')) return true;
  if (searchParams.get(PRE_ONBOARDING_JOURNEY_RETURN_PARAM) === '1') return true;
  return isPreOnboardingLegacyModulePath(path);
}

function ongoingHubHasDeepState(searchParams: URLSearchParams): boolean {
  return ONGOING_DEEP_QUERY_KEYS.some((key) => searchParams.has(key));
}

function resolvePreOnboardingParentHref(path: string): string | null {
  if (PRE_ONBOARDING_HUB_PATHS.has(path)) return null;
  if (path.startsWith('/vms/pre-onboarding/vendor-initiation')) {
    return '/vms/pre-onboarding/vendor-initiation';
  }
  if (path.startsWith('/vms/pre-onboarding/in-progress')) {
    return '/vms/pre-onboarding/in-progress';
  }
  if (path.startsWith('/vms/pre-onboarding/overview')) {
    return '/vms/pre-onboarding/overview';
  }
  return '/vms/pre-onboarding/in-progress';
}

/**
 * When the header service picker changes, deep routes under Pre Onboarding / Onboarding /
 * Ongoing Management should reset to that section's hub so lists and strips match the selection.
 * Returns null when already on the hub (no navigation needed).
 */
export function resolveVmsWorkspaceParentNavHref(pathname: string, search = ''): string | null {
  const path = normalizePath(pathname);
  const searchParams = parseSearch(search);

  if (isPreOnboardingWorkspaceRoute(path, searchParams)) {
    return resolvePreOnboardingParentHref(path);
  }

  if (path === ONBOARDING_HUB_PATH) return null;
  if (path.startsWith('/vms/onboarding') || path.startsWith('/vms/onboarding2')) {
    return ONBOARDING_HUB_PATH;
  }

  if (path.startsWith('/vms/ongoing2/')) {
    return ONGOING_HUB_PATH;
  }
  if (path.startsWith('/vms/ongoing/')) {
    return ONGOING_HUB_PATH;
  }
  if (path === ONGOING_HUB_PATH) {
    return ongoingHubHasDeepState(searchParams) ? ONGOING_HUB_PATH : null;
  }
  if (path.startsWith('/vms/ongoing2')) {
    return ONGOING_HUB_PATH;
  }

  return null;
}
